import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../App';

const BillingContext = createContext(null);

export const useBilling = () => useContext(BillingContext);

// iter1106 - tier rank + feature-min-tier fallback used when the client
// hasn't yet fetched /billing/config (first paint). Backend is source of
// truth; this constant mirrors services/billing_plans.py:FEATURE_MIN_TIER
// so the UI can hide the Payroll tile even before the config lands.
const TIER_RANK = { starter: 0, growth: 1, standard: 1, pro: 2 };
const FEATURE_MIN_TIER_FALLBACK = { payroll: 'growth' };

export function BillingProvider({ children }) {
  const { user, business } = useAuth() || {};
  const [billingStatus, setBillingStatus] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const userId = user?.id;
  const businessId = business?.id;

  const fetchBillingStatus = useCallback(async () => {
    if (!userId || !businessId) {
      setBillingStatus(null);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get(`/billing/status`);
      setBillingStatus(res.data);
    } catch {
      setBillingStatus(null);
    } finally {
      setLoading(false);
    }
  }, [userId, businessId]);

  // iter1106 - public billing config (trial length, feature-gate map,
  // reactivation matrix). Cached in-memory; doesn't require auth.
  useEffect(() => {
    let cancelled = false;
    apiClient.get('/billing/config')
      .then((r) => { if (!cancelled) setPublicConfig(r.data); })
      .catch(() => { /* keep fallback defaults */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchBillingStatus();
  }, [fetchBillingStatus]);

  // Onboarding creates the business a few seconds before the trial
  // subscription exists. If the first fetch lands in that gap we'd cache
  // "no_subscription" for the whole session, so retry briefly.
  const noSubStatus = billingStatus?.status === 'no_subscription' || billingStatus?.status === 'no_business';
  useEffect(() => {
    if (!noSubStatus || !userId || !businessId) return undefined;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > 6) { clearInterval(timer); return; }
      fetchBillingStatus();
    }, 5000);
    return () => clearInterval(timer);
  }, [noSubStatus, userId, businessId, fetchBillingStatus]);

  const isRestricted = billingStatus?.status === 'restricted_view_only';
  const isLocked = billingStatus?.status === 'hard_lock_billing_only';
  const isPastDue = billingStatus?.status === 'past_due';
  // First payment never succeeded (no-grace policy) - pending or past_due
  const neverPaid = !!billingStatus?.never_paid && ['pending', 'past_due'].includes(billingStatus?.status);
  const isActive = ['active', 'trialing', 'past_due'].includes(billingStatus?.status);

  // iter1106 - tier + feature availability derived on-demand for consumers.
  // billing/status returns `plan_tier` at the top level (existing shape) plus
  // the new `tier` alias we added in iter1106. Read both to stay
  // backward-compatible with any older backend still in flight during rollout.
  const tier = billingStatus?.plan_tier || billingStatus?.tier || billingStatus?.plan?.tier || null;
  const featureMinTier = publicConfig?.feature_min_tier || FEATURE_MIN_TIER_FALLBACK;
  const grandfathered = new Set(billingStatus?.grandfathered_features || []);

  const supportsFeature = (feature) => {
    if (grandfathered.has(feature)) return true;
    const required = featureMinTier[feature];
    if (!required) return true;
    const tierNorm = tier === 'standard' ? 'growth' : tier;
    return (TIER_RANK[tierNorm] ?? -1) >= (TIER_RANK[required] ?? 0);
  };

  const features = {
    payroll: supportsFeature('payroll'),
  };

  const trialDays = publicConfig?.trial_days ?? 3;

  return (
    <BillingContext.Provider value={{
      billingStatus,
      loading,
      isRestricted,
      isLocked,
      isPastDue,
      neverPaid,
      isActive,
      refreshBilling: fetchBillingStatus,
      // iter1106
      tier,
      features,
      supportsFeature,
      trialDays,
      publicConfig,
    }}>
      {children}
    </BillingContext.Provider>
  );
}
